/**
 * Notification Channel Escalation Mycelium Connector
 *
 * Implements intelligent notification routing with priority-based escalation.
 * Automatically escalates urgent notifications through multiple channels.
 *
 * Key Features:
 * - Priority-based routing (P0/P1/P2/P3)
 * - Channel escalation: Push → Discord → WhatsApp → Email
 * - Time-based escalation (no acknowledgment → escalate)
 * - Smart deduplication across channels
 * - Configurable escalation rules per notification type
 *
 * Flow Example:
 * 1. Critical alert (P0): "Production database down"
 *    → Immediate push notification
 *    → If no ack in 2 min → Discord ping
 *    → If no ack in 5 min → WhatsApp message
 *    → If no ack in 10 min → Email + SMS
 *
 * @module mycelium-notification-escalation
 * @since 2026-02-20
 */

import { myceliumEventBackbone, type MyceliumEvent } from "./mycelium-event-backbone.service";
import { getMyceliumTransport } from "./mycelium-transport-registry";
import { logger } from "../lib/logger";
import { typedSupabase } from "../lib/typed-supabase";

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Priority levels for notifications
 */
enum NotificationPriority {
  P0_CRITICAL = 0,    // System-breaking, immediate attention
  P1_HIGH = 1,        // Important, needs quick attention
  P2_MEDIUM = 2,      // Normal priority
  P3_LOW = 3,         // Informational
}

/**
 * Escalation configuration by priority
 */
const ESCALATION_CONFIG = {
  [NotificationPriority.P0_CRITICAL]: {
    channels: ['push', 'discord', 'whatsapp', 'sms', 'email'],
    delays: [0, 120000, 300000, 600000],  // 0s, 2min, 5min, 10min
    retryCount: 5,
  },
  [NotificationPriority.P1_HIGH]: {
    channels: ['push', 'discord', 'email'],
    delays: [0, 300000, 900000],  // 0s, 5min, 15min
    retryCount: 3,
  },
  [NotificationPriority.P2_MEDIUM]: {
    channels: ['push', 'email'],
    delays: [0, 1800000],  // 0s, 30min
    retryCount: 2,
  },
  [NotificationPriority.P3_LOW]: {
    channels: ['push'],
    delays: [0],  // No escalation
    retryCount: 1,
  },
} as const;

/**
 * Notification types that should escalate
 */
const ESCALATION_TRIGGERS = [
  'system_error',
  'security_alert',
  'payment_failed',
  'deadline_missed',
  'task_overdue',
  'approval_required',
  'critical_update',
] as const;

// ============================================================================
// TYPES
// ============================================================================

interface NotificationEscalation {
  notificationId: string;
  userId: string;
  priority: NotificationPriority;
  currentChannel: string;
  escalationLevel: number;
  acknowledged: boolean;
  escalationTimers: NodeJS.Timeout[];
  metadata: Record<string, unknown>;
}

// ============================================================================
// CONNECTOR SERVICE
// ============================================================================

export class NotificationChannelEscalationConnector {
  private initialized = false;
  private activeEscalations = new Map<string, NotificationEscalation>();

  /**
   * Initialize connector and subscribe to events
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      logger.debug("NotificationChannelEscalationConnector already initialized");
      return;
    }

    logger.info("🔔 Notification Channel Escalation connector initializing");

    // Subscribe to notification domain events
    myceliumEventBackbone.subscribe({
      name: "NotificationChannelEscalationConnector-Notifications",
      domains: ["notification"],
      handler: this.handleNotificationEvent.bind(this),
      priority: 9, // Highest priority for urgent notifications
    });

    // Subscribe to acknowledgment events
    myceliumEventBackbone.subscribe({
      name: "NotificationChannelEscalationConnector-Acknowledgments",
      domains: ["notification"],
      eventTypes: ["notification_acknowledged", "notification_dismissed"],
      handler: this.handleAcknowledgment.bind(this),
      priority: 10, // Even higher to cancel escalations quickly
    });

    this.initialized = true;
    logger.info("✅ Notification Channel Escalation connector ready");
    logger.info("💡 Critical notifications will auto-escalate across channels");
  }

  /**
   * Handle notification events
   */
  private async handleNotificationEvent(event: MyceliumEvent): Promise<void> {
    const { type, userId, payload } = event;

    // Skip acknowledgment events (handled separately)
    if (type === 'notification_acknowledged' || type === 'notification_dismissed') {
      return;
    }

    logger.debug("🔔 Notification event received", {
      eventId: event.id,
      type,
      userId,
    });

    try {
      // Extract notification details
      const notificationId = payload.notificationId as string || payload.id as string;
      const notificationType = payload.type as string || type;
      const priority = this.determinePriority(notificationType, payload);

      // Check if notification should escalate
      if (!this.shouldEscalate(notificationType, priority)) {
        logger.debug("Notification does not require escalation", {
          notificationId,
          type: notificationType,
          priority,
        });
        return;
      }

      logger.info("🚨 Starting escalation for notification", {
        notificationId,
        type: notificationType,
        priority,
        userId,
      });

      // Start escalation chain
      await this.startEscalation({
        notificationId,
        userId: userId || '',
        priority,
        currentChannel: 'push',
        escalationLevel: 0,
        acknowledged: false,
        escalationTimers: [],
        metadata: {
          type: notificationType,
          payload,
          startedAt: new Date().toISOString(),
        },
      }, event);
    } catch (error) {
      logger.error("Error handling notification event", {
        eventId: event.id,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  /**
   * Handle acknowledgment events (stop escalation)
   */
  private async handleAcknowledgment(event: MyceliumEvent): Promise<void> {
    const { payload } = event;

    try {
      const notificationId = payload.notificationId as string || payload.id as string;

      logger.debug("✅ Notification acknowledged", { notificationId });

      // Cancel any pending escalations
      const escalation = this.activeEscalations.get(notificationId);
      if (escalation) {
        escalation.acknowledged = true;

        // Clear all timers
        for (const timer of escalation.escalationTimers) {
          clearTimeout(timer);
        }

        this.activeEscalations.delete(notificationId);

        logger.info("🛑 Escalation stopped", {
          notificationId,
          stoppedAtLevel: escalation.escalationLevel,
          channel: escalation.currentChannel,
        });

        // Publish escalation_stopped event
        await getMyceliumTransport().publish({
          domain: "notification",
          type: "escalation_stopped",
          userId: escalation.userId,
          payload: {
            notificationId,
            stoppedAtLevel: escalation.escalationLevel,
            acknowledgedChannel: escalation.currentChannel,
          },
        });
      }
    } catch (error) {
      logger.error("❌ NotificationEscalation acknowledgment handler error", {
        eventId: event.id,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  }

  /**
   * Start escalation chain
   */
  private async startEscalation(
    escalation: NotificationEscalation,
    sourceEvent: MyceliumEvent
  ): Promise<void> {
    // Store active escalation
    this.activeEscalations.set(escalation.notificationId, escalation);

    // Delegate delayed escalation to worker + QStash (server-side, durable)
    await getMyceliumTransport().publish({
      domain: "notification",
      type: "notification_delivery_requested",
      userId: escalation.userId,
      payload: {
        notificationId: escalation.notificationId,
        escalationLevel: 0,
        priority: escalation.priority,
        scheduleEscalation: true,
        source: "NotificationEscalationConnector",
      },
    });
  }

  /**
   * Send notification through specific channel
   */
  private async sendThroughChannel(
    channel: string,
    escalation: NotificationEscalation,
    sourceEvent: MyceliumEvent
  ): Promise<void> {
    logger.info(`📨 Sending notification via ${channel}`, {
      notificationId: escalation.notificationId,
      level: escalation.escalationLevel,
      priority: escalation.priority,
    });

    // Publish channel-specific event
    await getMyceliumTransport().publish({
      domain: "notification",
      type: `notification_send_${channel}`,
      userId: escalation.userId,
      payload: {
        notificationId: escalation.notificationId,
        channel,
        priority: escalation.priority,
        escalationLevel: escalation.escalationLevel,
        message: escalation.metadata,
        sourceEventId: sourceEvent.id,
      },
    });
  }

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Determine notification priority based on type and payload
   */
  private determinePriority(
    type: string,
    payload: Record<string, unknown>
  ): NotificationPriority {
    // Explicit priority in payload
    const explicitPriority = payload.priority as number | string;
    if (typeof explicitPriority === 'number') {
      return explicitPriority as NotificationPriority;
    }

    // Priority based on type
    if (type.includes('critical') || type.includes('error')) {
      return NotificationPriority.P0_CRITICAL;
    }

    if (type.includes('urgent') || type.includes('security') || type.includes('payment')) {
      return NotificationPriority.P1_HIGH;
    }

    if (type.includes('deadline') || type.includes('overdue') || type.includes('approval')) {
      return NotificationPriority.P1_HIGH;
    }

    // Default to medium
    return NotificationPriority.P2_MEDIUM;
  }

  /**
   * Check if notification type should escalate
   */
  private shouldEscalate(type: string, priority: NotificationPriority): boolean {
    // Always escalate P0 and P1
    if (priority === NotificationPriority.P0_CRITICAL ||
        priority === NotificationPriority.P1_HIGH) {
      return true;
    }

    // Check if type is in escalation triggers
    return ESCALATION_TRIGGERS.some(trigger =>
      type.toLowerCase().includes(trigger)
    );
  }

  /**
   * Cleanup on disposal
   */
  async dispose(): Promise<void> {
    // Cancel all active escalations
    for (const [id, escalation] of this.activeEscalations.entries()) {
      for (const timer of escalation.escalationTimers) {
        clearTimeout(timer);
      }
      this.activeEscalations.delete(id);
    }

    this.initialized = false;
    logger.info("🧹 Notification Channel Escalation connector disposed");
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

export const notificationChannelEscalationConnector = new NotificationChannelEscalationConnector();
